import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { Search, Camera } from "lucide-react";
import { Input } from "../components/ui/input";
import { Switch } from "../components/ui/switch";
import ProductCard from "../components/ProductCard";

export default function Shop() {
    const [params, setParams] = useSearchParams();
    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [ratings, setRatings] = useState({});
    const [q, setQ] = useState(params.get("q") || "");
    const activeCat = params.get("category") || "";
    const onlyPerSizePhotos = params.get("perSize") === "1";

    useEffect(() => {
        api.get("/categories").then(r => setCategories(r.data)).catch(() => {});
    }, []);

    useEffect(() => {
        const fetchProducts = async () => {
            const { data } = await api.get("/products", { params: { category_id: activeCat || undefined, q: q || undefined } });
            setProducts(data);
            if (data.length) {
                try {
                    const ids = data.map(p => p.id).join(",");
                    const sum = await api.get("/reviews/summary-many", { params: { target_type: "product", ids } });
                    setRatings(sum.data || {});
                } catch { /* non-blocking */ }
            }
        };
        fetchProducts();
    }, [activeCat, q]);

    const setCat = (id) => {
        const p = new URLSearchParams(params);
        if (id) p.set("category", id); else p.delete("category");
        setParams(p);
    };

    const togglePerSize = (on) => {
        const p = new URLSearchParams(params);
        if (on) p.set("perSize", "1"); else p.delete("perSize");
        setParams(p);
    };

    // How many products have at least one variant with its own photos —
    // surfaced on the toggle row so the filter never feels like a black box.
    const perSizeCount = useMemo(
        () => products.filter((p) => (p.variants || []).some((v) => v.images && v.images.length > 0)).length,
        [products],
    );

    const visible = useMemo(() => {
        // Sort products by their lowest sellable variant price ascending — so
        // the "from ₹X" badges read low → high as the customer scans down the
        // grid. Products with no priced variants sink to the bottom.
        const minPrice = (p) => {
            const prices = (p.variants || []).map((v) => Number(v.price) || 0).filter((n) => n > 0);
            return prices.length === 0 ? Number.POSITIVE_INFINITY : Math.min(...prices);
        };
        const base = onlyPerSizePhotos
            ? products.filter((p) => (p.variants || []).some((v) => v.images && v.images.length > 0))
            : products;
        return [...base].sort((a, b) => minPrice(a) - minPrice(b));
    }, [products, onlyPerSizePhotos]);

    return (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="shop-page">
            <div className="label-eyebrow text-sky-700">The pharmacy</div>
            <h1 className="font-serif text-5xl text-stone-900 mt-2 mb-10">All medicines</h1>

            <div className="grid lg:grid-cols-12 gap-10">
                <aside className="lg:col-span-3 space-y-8">
                    <div>
                        <div className="relative">
                            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
                            <Input
                                value={q}
                                onChange={(e) => setQ(e.target.value)}
                                placeholder="Search remedies…"
                                className="pl-9 rounded-none border-stone-300"
                                data-testid="shop-search"
                            />
                        </div>
                    </div>
                    <div>
                        <div className="label-eyebrow text-stone-500 mb-3">Categories</div>
                        <ul className="space-y-1">
                            <li>
                                <button onClick={() => setCat("")} className={`text-sm w-full text-left py-1.5 ${!activeCat ? "text-sky-700 font-medium" : "text-stone-700 hover:text-sky-700"}`} data-testid="filter-all">All medicines</button>
                            </li>
                            {categories.map((c) => (
                                <li key={c.id}>
                                    <button onClick={() => setCat(c.id)} className={`text-sm w-full text-left py-1.5 ${activeCat === c.id ? "text-sky-700 font-medium" : "text-stone-700 hover:text-sky-700"}`} data-testid={`filter-${c.slug}`}>
                                        {c.name}
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div>
                        <div className="label-eyebrow text-stone-500 mb-3">Refine</div>
                        <label
                            className={`flex items-start gap-3 border px-3 py-3 cursor-pointer transition-colors ${onlyPerSizePhotos ? "border-sky-700 bg-sky-50" : "border-stone-200 hover:border-stone-400"} ${perSizeCount === 0 ? "opacity-50 cursor-not-allowed" : ""}`}
                            data-testid="filter-per-size-wrap"
                        >
                            <Camera className={`w-4 h-4 mt-0.5 shrink-0 ${onlyPerSizePhotos ? "text-sky-700" : "text-stone-400"}`} />
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center justify-between gap-2">
                                    <span className="text-sm font-medium text-stone-800">Has custom size photos</span>
                                    <Switch
                                        checked={onlyPerSizePhotos}
                                        onCheckedChange={togglePerSize}
                                        disabled={perSizeCount === 0}
                                        data-testid="filter-per-size-toggle"
                                    />
                                </div>
                                <p className="text-[11px] text-stone-500 mt-1 leading-snug">
                                    {perSizeCount === 0
                                        ? "No products have per-size photography yet."
                                        : `Surface only the ${perSizeCount} product${perSizeCount === 1 ? "" : "s"} where each size has its own photo.`}
                                </p>
                            </div>
                        </label>
                    </div>
                </aside>

                <div className="lg:col-span-9">
                    {visible.length === 0 ? (
                        <div className="border border-stone-200 bg-white p-12 text-center" data-testid="shop-empty">
                            <h3 className="font-serif text-2xl text-stone-900">
                                {onlyPerSizePhotos && products.length > 0 ? "No products match this filter" : "No products yet"}
                            </h3>
                            <p className="text-sm text-stone-600 mt-2">
                                {onlyPerSizePhotos && products.length > 0
                                    ? "Try turning off \"Has custom size photos\" to see the full catalog."
                                    : "The admin can add products from the admin panel."}
                            </p>
                        </div>
                    ) : (
                        <>
                            {onlyPerSizePhotos && (
                                <div className="flex items-center justify-between mb-4 text-xs text-stone-500 flex-wrap gap-2" data-testid="shop-result-summary">
                                    <span>
                                        Showing <strong className="text-stone-800">{visible.length}</strong> product{visible.length === 1 ? "" : "s"} with custom size photography.
                                    </span>
                                    <button onClick={() => togglePerSize(false)} className="text-sky-700 underline hover:text-sky-800" data-testid="shop-clear-per-size">
                                        Show all medicines
                                    </button>
                                </div>
                            )}
                            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                                {visible.map((p) => (
                                    <ProductCard
                                        key={p.id}
                                        product={p}
                                        rating={ratings[p.id]}
                                    />
                                ))}
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
